import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Button,
  ConfidenceBar,
  DataTable,
  EmptyState,
  PageHeader,
  StatCard,
  StatGrid,
  StatusChip,
  Tabs,
  Tag,
} from '@/design/components'
import type { Column } from '@/design/components'
import { useMe } from '@/app/MeContextProvider'
import { EMPTY, formatDate, plural } from '@/lib/format'
import { cn } from '@/lib/cn'
import { useDocuments, useInboxStats, useRetryDocument } from '../hooks'
import { SOURCE_LABEL, SOURCE_ORDER, STATUS_LABEL, STATUS_VARIANT, receivedShort } from '../labels'
import type { DocumentRow, DocumentSource } from '../types'
import { UploadModal } from '../components/UploadModal'

const PAGE_SIZE = 50
const ALL = 'all'

/** „4 str. · dziś 09:14" — druga linia w kolumnie DOKUMENT. */
function receivedLabel(row: DocumentRow): string {
  const pages = row.pages ? `${row.pages} str.` : EMPTY
  return `${pages} · ${receivedShort(row.received_at)}`
}

/** Miniatura dokumentu (placeholder — podgląd strony tytułowej to v2). */
function Thumb() {
  return (
    <span
      aria-hidden
      className="block h-[30px] w-[26px] shrink-0 rounded-[2px] border border-line-soft bg-[repeating-linear-gradient(135deg,#eef1f4_0_4px,#f7f9fa_4px_8px)]"
    />
  )
}

export function InboxPage() {
  const navigate = useNavigate()
  const { can } = useMe()
  const [page, setPage] = useState(1)
  const [source, setSource] = useState<string>(ALL)
  const [uploadOpen, setUploadOpen] = useState(false)

  // Wejście z pulpitu („Prześlij dokument" → /dokumenty/skrzynka?upload=1) otwiera modal
  // uploadu i od razu czyści query param, żeby nie wracał przy nawigacji wstecz.
  const [searchParams, setSearchParams] = useSearchParams()
  useEffect(() => {
    if (searchParams.get('upload') !== '1') return
    const next = new URLSearchParams(searchParams)
    next.delete('upload')
    setSearchParams(next, { replace: true })
    if (can('documents.inbox')) setUploadOpen(true)
  }, [searchParams, setSearchParams, can])

  const stats = useInboxStats()
  const listing = useDocuments({
    page,
    size: PAGE_SIZE,
    source: source === ALL ? undefined : source,
  })

  const rows = listing.data?.items ?? []
  const bySource = stats.data?.by_source ?? {}
  const retry = useRetryDocument()
  const canVerify = can('documents.verify')

  const pills = useMemo(
    () => [
      { key: ALL, label: 'Wszystkie', count: stats.data?.total ?? 0 },
      ...SOURCE_ORDER.filter((value) => bySource[value] !== undefined).map((value) => ({
        key: value,
        label: SOURCE_LABEL[value as DocumentSource],
        count: bySource[value] ?? 0,
      })),
    ],
    [bySource, stats.data?.total],
  )

  const columns: Column<DocumentRow>[] = [
    {
      key: 'document',
      header: 'Dokument',
      render: (row) => (
        <div className="flex min-w-0 items-center gap-3">
          <Thumb />
          <div className="min-w-0 leading-tight">
            <p className="truncate font-semibold text-ink">{row.title}</p>
            <p className="mt-0.5 truncate text-small text-muted">{receivedLabel(row)}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'source',
      header: 'Źródło',
      width: '118px',
      render: (row) => <span className="text-muted">{SOURCE_LABEL[row.source]}</span>,
    },
    {
      key: 'doc_type',
      header: 'Typ (AI)',
      width: '146px',
      render: (row) => (row.doc_type ? <Tag>{row.doc_type}</Tag> : <span className="text-muted">{EMPTY}</span>),
    },
    {
      key: 'confidence',
      header: 'Pewność',
      width: '140px',
      render: (row) => <ConfidenceBar value={row.confidence} width={132} />,
    },
    {
      key: 'case',
      header: 'Sugerowana sprawa',
      width: '190px',
      render: (row) => {
        const target = row.case ?? row.suggested_case
        if (!target) {
          return (
            <div className="leading-tight">
              <p className="t-mono text-danger">brak dopasowania</p>
              <p className="mt-0.5 text-small text-muted">wymaga przypisania</p>
            </div>
          )
        }
        return (
          <div className="min-w-0 leading-tight">
            <p className="t-mono truncate text-ink">{target.signature}</p>
            <p className="mt-0.5 truncate text-small text-muted">{target.client_name}</p>
          </div>
        )
      },
    },
    {
      key: 'detected_deadline',
      header: 'Wykryty termin',
      width: '128px',
      render: (row) =>
        row.detected_deadline ? (
          <span className="font-medium text-danger">{formatDate(row.detected_deadline)}</span>
        ) : (
          <span className="text-muted">{EMPTY}</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '164px',
      render: (row) => (
        <div className="flex flex-wrap items-center gap-2">
          <StatusChip variant={STATUS_VARIANT[row.status]}>{STATUS_LABEL[row.status]}</StatusChip>
          {/* Awaria pipeline'u to jedyny status bez naturalnego wyjścia w UI — bez tego
              przycisku dokument zostawał w skrzynce na zawsze. */}
          {row.status === 'failed' && canVerify ? (
            <button
              type="button"
              className="text-micro font-medium text-navy underline underline-offset-2 hover:text-gold disabled:opacity-50"
              disabled={retry.isPending}
              title="Wróć do kolejki AI i przetwórz dokument jeszcze raz"
              onClick={(event) => {
                event.stopPropagation()
                retry.mutate(row.id)
              }}
            >
              Ponów
            </button>
          ) : null}
        </div>
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title="Skrzynka dokumentów"
        subtitle="Dokumenty przychodzące po OCR i analizie AI. Weryfikacja przez sekretariat przed dekretacją."
        actions={
          <>
            <Button variant="secondary" disabled title="Reguły klasyfikacji — planowane w v2">
              Reguły klasyfikacji
            </Button>
            {can('documents.inbox') ? (
              <Button variant="primary" onClick={() => setUploadOpen(true)}>
                Prześlij skan
              </Button>
            ) : null}
          </>
        }
      />

      <StatGrid className="mb-5">
        <StatCard
          layout="value-top"
          accent="navy"
          label="Wpływ dzisiaj"
          value={stats.data?.today ?? EMPTY}
          loading={stats.isPending}
        />
        <StatCard
          layout="value-top"
          accent="gold"
          label="Do weryfikacji"
          value={stats.data?.to_verify ?? EMPTY}
          loading={stats.isPending}
        />
        <StatCard
          layout="value-top"
          accent="danger"
          label="Niska pewność AI"
          value={stats.data?.low_confidence ?? EMPTY}
          loading={stats.isPending}
        />
        <StatCard
          layout="value-top"
          accent="success"
          label="Zdekretowane"
          value={stats.data?.decreed ?? EMPTY}
          loading={stats.isPending}
        />
      </StatGrid>

      <div className="am-card mb-4 flex flex-wrap items-center justify-between gap-3 px-4 py-3">
        <Tabs
          variant="pills"
          items={pills.map((pill) => ({ key: pill.key, label: pill.label, count: pill.count }))}
          value={source}
          onChange={(key) => {
            setSource(key)
            setPage(1)
          }}
        />
        {/* Etykieta, nie kontrolka: lista jest zawsze sortowana po dacie wpływu malejąco
            (`ORDER BY received_at DESC` w API) i nie da się tego tutaj zmienić. */}
        <span className="text-micro text-muted">Sortowanie: od najnowszego wpływu</span>
      </div>

      <DataTable<DocumentRow>
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        onRowClick={(row) => navigate(`/dokumenty/weryfikacja/${row.id}`)}
        total={listing.data?.total ?? 0}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        loading={listing.isPending}
        className={cn(listing.isFetching && 'opacity-95')}
        empty={
          <EmptyState
            title="Skrzynka jest pusta"
            description="Nowe pisma pojawią się tu po analizie OCR i modelu."
            action={
              can('documents.inbox') ? (
                <Button variant="primary" onClick={() => setUploadOpen(true)}>
                  Prześlij skan
                </Button>
              ) : null
            }
          />
        }
      />

      <p className="mt-3 text-micro text-muted">
        {listing.data
          ? `${listing.data.total} ${plural(listing.data.total, 'dokument', 'dokumenty', 'dokumentów')} w skrzynce`
          : ''}
      </p>

      <UploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={(id) => navigate(`/dokumenty/weryfikacja/${id}`)}
      />
    </>
  )
}
